import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { take, withLatestFrom, map } from 'rxjs/operators';
import moment from 'moment';
import { generateExcel } from '../components/excel-export/excel.generator';
import { ExportMonitorService } from '../../monitor/services/export-monitor.service';
import { ExportReportsService } from '../../reports/services/export-reports.service';
import { ExportOrdersService } from '../../all-orders/services/export-orders.service';
import { ReportType, ReportTypes, TabSettings } from '../models/report';

const MAX_FILE_NAME_LENGTH = 100;

@Injectable()
export class ExportService {
  public readonly hasAccountsPermissions$: Observable<boolean> = this
    .exportReportsService.hasAccountsPermissions$;
  public readonly isExportAllowed$: Observable<boolean> = this
    .exportOrdersService.isAllowed$;
  private readonly fileName$: Observable<string> = this.exportOrdersService.deal$.pipe(
    map((deal) => this.getFileName(deal))
  );

  constructor(
    private exportMonitorService: ExportMonitorService,
    private exportReportsService: ExportReportsService,
    private exportOrdersService: ExportOrdersService
  ) {}

  public export(reports: ReportTypes): void {
    combineLatest(
      this.fileName$,
      ...this.getExportData(reports),
      (name: string, ...data: TabSettings[][]) => ({
        data,
        name
      })
    )
      .pipe(take(1))
      .subscribe(({ data, name }) => {
        const tabs = [].concat(...data).sort((a, b) => a.priority - b.priority);

        generateExcel({ name, tabs });
      });
  }

  public subscribe() {
    this.exportMonitorService.subscribe();
  }

  public unsubscribe() {
    this.exportMonitorService.unsubscribe();
  }

  private getFileName({ description }): string {
    const exportStamp = moment().format('L LTS');
    let fileName = `${description} - ${exportStamp}_Confidential.xlsx`;
    const diff: number = fileName.length - MAX_FILE_NAME_LENGTH;

    if (diff > 0) {
      const issueName: string = description.substr(
        0,
        description.length - diff - 3
      );
      fileName = `${issueName}... - ${exportStamp}_Confidential.xlsx`;
    }

    return fileName;
  }

  private getExportData(reports: ReportTypes) {
    const exportData: Array<Observable<TabSettings[]>> = [];
    const reportChecker = reports.reduce(
      (prev, current) => ({ ...prev, [current]: !!current }),
      {}
    );

    if (reportChecker[ReportType.AllOrders]) {
      exportData.push(this.exportOrdersService.ordersData$);
    }

    if (reportChecker[ReportType.OrderMonitor]) {
      exportData.push(this.exportMonitorService.monitorData$);
    }

    if (
      reportChecker[ReportType.ManagersReport] ||
      reportChecker[ReportType.AccountsReport]
    ) {
      const reportsData$: Observable<
        TabSettings[]
      > = this.exportReportsService.reportsData$.pipe(
        withLatestFrom(this.hasAccountsPermissions$),
        map(([tabs, hasAccountsPermissions]) => {
          if (hasAccountsPermissions) {
            if (
              reportChecker[ReportType.ManagersReport] &&
              reportChecker[ReportType.AccountsReport]
            ) {
              return tabs;
            } else if (reportChecker[ReportType.ManagersReport]) {
              return tabs.slice(0, -1);
            } else if (reportChecker[ReportType.AccountsReport]) {
              return tabs.slice(-1);
            }
          }

          return tabs;
        })
      );

      exportData.push(reportsData$);
    }

    return exportData;
  }
}
